import { CSSProperties, useEffect, useState } from "react";
import { CoreLoadingPage, CoreNoResults } from "@verity/components/core";
import ProctoringResultView from "../results/components/student-selection/videoplayer/ProctoringResultView";
import { fetchDemoResultsBootstrap, installDemoResultsApi, type DemoResultsBootstrap } from "./demo-results-api";

const PANEL_STYLE: CSSProperties = {
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	minHeight: 480,
	padding: 24,
};

const UNAVAILABLE_HEADER = "The demo report is unavailable right now.";

type State =
	{ kind: "loading" } | { kind: "unavailable"; failed: boolean } | { kind: "ready"; bootstrap: DemoResultsBootstrap };

/** `preparedFor` is the visitor's name from the lead form, not the fixture test-taker's. */
export default function DemoResult({ preparedFor }: { preparedFor: string }) {
	const [state, setState] = useState<State>({ kind: "loading" });

	useEffect(() => {
		let active = true;
		installDemoResultsApi();
		fetchDemoResultsBootstrap()
			.then((bootstrap) => {
				if (!active) return;
				// An empty `proctoringSessions` may not mount the view: `sessionUuid` would be undefined and
				// every session-scoped request would fire against it.
				setState(
					bootstrap.proctoringSessions.length === 0
						? { kind: "unavailable", failed: false }
						: { kind: "ready", bootstrap },
				);
			})
			.catch(() => {
				if (active) setState({ kind: "unavailable", failed: true });
			});
		return () => {
			active = false;
		};
	}, []);

	if (state.kind === "loading") {
		return (
			<div style={PANEL_STYLE}>
				<CoreLoadingPage />
			</div>
		);
	}

	if (state.kind === "unavailable") {
		return (
			<div style={PANEL_STYLE}>
				<CoreNoResults header={UNAVAILABLE_HEADER} variant={state.failed ? "failed" : "no-results"} />
			</div>
		);
	}

	const { bootstrap } = state;
	const first = bootstrap.proctoringSessions[0];
	return (
		// `studentList` holds exactly one entry: an empty list makes the built-in prev/next compute
		// `(-1 + 1) % 0` → `NaN` and index into the array. `setShowStudentResults` is deliberately not
		// passed, so the view's own back button disappears with it — the demo page owns that control.
		<ProctoringResultView
			instituteId={bootstrap.instituteId}
			courseId={bootstrap.courseId}
			showVideo
			readOnly
			hideConfigurationsTab
			hideActionsMenu
			proctoringSessions={bootstrap.proctoringSessions}
			liveSessions={{}}
			assessmentInfo={bootstrap.assessmentInfo}
			selectedStudent={bootstrap.studentId}
			studentList={[
				{
					id: bootstrap.studentId,
					name: first?.userInfo?.full_name || "",
					severityLevel: first?.flagLevel || undefined,
				},
			]}
			setSelectedStudent={() => {}}
			// The test-taker block is this card's title instead, rendered through the view's own prop rather
			// than swapped in CSS, so the label a screen reader announces is the one on screen.
			testTakerHeading={{ label: "Session Report", value: `Prepared for ${preparedFor}` }}
		/>
	);
}
